import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import {useSessionConfiguration} from 'util/SessionConfigurationContext';
import {stringOneLetterDifference} from 'util/language/ordinal';

const UniversalInputField = forwardRef(({
                                            allowedLetters,
                                            allowedEmojis,
                                            isInputDisabled,
                                            onInputChange,
                                            onEnterPress,
                                            inputPlaceholder,
                                            showErroredPlaceholder = false,
                                            maxInputLength,
                                            setInputLength,
                                            realKeyboardKeyUp,
                                            realKeyboardKeyDown,
                                            mobileKeyPressed,
                                        }, ref) => {
    const [input, setInput] = useState('');
    const [cursorPosition, setCursorPosition] = useState(null);
    const inputRef = useRef(null);
    const previousInputRef = useRef('');
    const {isTouch} = useSessionConfiguration();

    useImperativeHandle(ref, () => ({
        insertKey(key) {
            const sanitizedKey = key.replace(/\uFE0F/g, '');
            const inputEl = inputRef.current;
            if (inputEl) {
                const start = inputEl.selectionStart;
                const end = inputEl.selectionEnd;
                const newValue = input.slice(0, start) + sanitizedKey + input.slice(end);
                if (newValue.length <= maxInputLength) {
                    const newCursorPosition = start + sanitizedKey.length;
                    setInput(newValue);
                    handleChange({target: {value: newValue}, artificial: true});
                    setCursorPosition(newCursorPosition);
                }
            }
        },
        clearInput() {
            setInput('');
        },
        callFocus() {
            inputRef.current.focus();
        },
        blur() {
            inputRef.current.blur();
        }
    }));

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = '45px'; // Reset height to one line
            const computed = window.getComputedStyle(inputRef.current);
            const lineHeight = parseFloat(computed.lineHeight);
            const maxHeight = lineHeight * 5 - 15; // Maximum of 5 lines
            inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, maxHeight) + 'px';
        }
    }, [input]);

    useEffect(() => {
        if (cursorPosition !== null) {
            inputRef.current.selectionStart = cursorPosition;
            inputRef.current.selectionEnd = cursorPosition;
            inputRef.current.focus();
            setCursorPosition(null);
        }
    }, [input, cursorPosition]);

    const handleChange = (event) => {
        const value = event.target.value.replace(/\uFE0F/g, '');
        const originalCursorPosition = event.target.selectionStart;
        const allowedSanitizedEmojis = allowedEmojis.map(emoji => emoji.replace(/\uFE0F/g, ''));

        let filteredValue = '';
        let removedValue = '';
        let newCursorPosition = 0;
        let localInputLength = 0;
        let i = 0;

        while (i < value.length && localInputLength < maxInputLength) {
            const char = value[i];
            let isAllowed = false;
            let charLength = 1;

            if (allowedLetters.includes(char)) {
                filteredValue += char;
                isAllowed = true;
            } else if (allowedSanitizedEmojis.includes(char)) {
                filteredValue += char;
                isAllowed = true;
            } else if (i < value.length - 1 && allowedSanitizedEmojis.includes(char + value[i + 1])) {
                filteredValue += char + value[i + 1];
                isAllowed = true;
                charLength = 2;
            } else {
                removedValue += char;
                isAllowed = false;
            }

            if (isAllowed) {
                localInputLength++;
                if (i + charLength <= originalCursorPosition) {
                    newCursorPosition += charLength;
                }
            }

            i += charLength;
        }

        if (setInputLength) {
            setInputLength(localInputLength);
        }

        setInput(filteredValue);
        setCursorPosition(newCursorPosition);
        onInputChange(filteredValue, removedValue);

        const originalInput = previousInputRef.current;
        previousInputRef.current = filteredValue;
        const letterAdded = stringOneLetterDifference(originalInput, value);
        if (!event.artificial && letterAdded && isTouch) {
            mobileKeyPressed(letterAdded);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onEnterPress();
        }
    };

    return (
        <textarea
            ref={inputRef}
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder}
            onKeyUpCapture={realKeyboardKeyUp}
            onKeyDownCapture={realKeyboardKeyDown}
            disabled={isInputDisabled}
            className={'chat-input' + (showErroredPlaceholder ? ' errored' : '')}
        />
    );
});

export default UniversalInputField;
